// Create a report for reconditioned vehicle passed in as a prop. Search for reconditioning-process collection and call all sub collections
// to get the data for the report.
// Report is to be in a tablular format with sections for each sub collection.
// Each section to have a header with the sub collection name and a table with the data.
// If no data is found for a sub collection, display "No data found".
// Use react-bootstrap for styling.
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Container, Row, Col, Table, Button, Spinner } from "react-bootstrap";
import {
  getFirestore,
  doc,
  getDoc,
  collection,
  getDocs,
} from "firebase/firestore";
import { useFunctions, useUser, useFirestore } from "reactfire";
import CryptoJS from "crypto-js";

const SPECIAL_COLOUR_YELLOW = "#D0872E";

// Form structure
const defaultOptionsInspection = [
  { label: "Yes", value: 1 },
  { label: "No", value: 0 },
  { label: "N/A", value: -1 },
];

const defaultOptionsQC = [
  { label: "Pass", value: 1 },
  { label: "Fail", value: 0 },
  { label: "N/A", value: -1 },
];

const formFieldsExterior = [
  {
    name: "washingAndDrying",
    label: "Washing and Drying",
    type: "radio",
    active: true,
    options: defaultOptionsQC,
    required: true,
  },
  {
    name: "paintCorrection",
    label: "Paint Correction (Touch up)",
    type: "radio",
    active: true,
    options: defaultOptionsQC,
    required: true,
    specialColour: SPECIAL_COLOUR_YELLOW,
  },
  {
    name: "polishingAndWaxing",
    label: "Polishing and Waxing",
    type: "radio",
    active: true,
    options: defaultOptionsQC,
    required: true,
  },
  {
    name: "wheelAndTyreCleaning",
    label: "Wheel and Tyre Cleaning",
    type: "radio",
    active: true,
    options: defaultOptionsQC,
    required: true,
  },
  {
    name: "windowCleaning",
    label: "Window Cleaning",
    type: "radio",
    active: true,
    options: defaultOptionsQC,
    required: true,
  },
  {
    name: "bugAndTarRemoval",
    label: "Bug and Tar Removal",
    type: "radio",
    active: true,
    options: defaultOptionsQC,
    required: true,
  },
  {
    name: "paintDecontamination",
    label: "Paint Decontamination",
    type: "radio",
    active: true,
    options: defaultOptionsQC,
    required: true,
  },
  {
    name: "minorRepairs",
    label: "Minor Repairs (dent)",
    type: "radio",
    active: true,
    options: defaultOptionsQC,
    required: true,
    specialColour: SPECIAL_COLOUR_YELLOW,
  },
];

const formFieldsInterior = [
  {
    name: "vacuumingAndCleaning",
    label: "Vacuuming and Cleaning",
    type: "radio",
    active: true,
    options: defaultOptionsQC,
    required: true,
  },
  {
    name: "upholsteryCleaning",
    label: "Upholstery Cleaning",
    type: "radio",
    active: true,
    options: defaultOptionsQC,
    required: true,
    specialColour: SPECIAL_COLOUR_YELLOW,
  },
  {
    name: "dashboardAndConsoleCleaning",
    label: "Dashboard and Console Cleaning",
    type: "radio",
    active: true,
    options: defaultOptionsQC,
    required: true,
  },
  {
    name: "deodorisation",
    label: "Deodorisation",
    type: "radio",
    active: true,
    options: defaultOptionsQC,
    required: true,
  },
  {
    name: "sanitisation",
    label: "Sanitisation",
    type: "radio",
    active: true,
    options: defaultOptionsQC,
    required: true,
  },
  {
    name: "leatherConditioning",
    label: "Leather Conditioning",
    type: "radio",
    active: true,
    options: defaultOptionsQC,
    required: true,
    specialColour: SPECIAL_COLOUR_YELLOW,
  },
  {
    name: "interiorGlassPolishing",
    label: "Interior Glass Polishing",
    type: "radio",
    active: true,
    options: defaultOptionsQC,
    required: true,
  },
  {
    name: "compartmentCleaning",
    label: "Compartment Cleaning",
    type: "radio",
    active: true,
    options: defaultOptionsQC,
    required: true,
  },
];

const formFieldsInspection = [
  {
    name: "serviceHistory",
    label: "Service History",
    type: "radio",
    active: true,
    options: [
      {
        label: "None",
        value: 0,
      },
      {
        label: "Partial",
        value: 1,
      },
      {
        label: "Full",
        value: 2,
      },
    ],
    required: true,
  },
  {
    name: "numberOfKeys",
    label: "Number of keys",
    type: "radio",
    active: true,
    options: [
      {
        label: "1",
        value: 1,
      },
      {
        label: "2",
        value: 2,
      },
    ],
  },
  {
    name: "externalScratchesOrDents",
    label:
      "External Scratches or dents (Panel shop, Paintless Dent Removal PDR, Touch up)",
    type: "radio",
    active: true,
    options: defaultOptionsInspection,
    required: true,
  },
  {
    name: "accidentSigns",
    label: "Any signs of being in an accident or accident repair?",
    type: "radio",
    active: true,
    options: defaultOptionsInspection,
    required: true,
  },
  {
    name: "frontTyres",
    label: "Front Tyre",
    type: "radio",
    active: true,
    options: defaultOptionsInspection,
    required: true,
  },
  {
    name: "frontPads",
    label: "Front Pads",
    type: "radio",
    active: true,
    options: defaultOptionsInspection,
    required: true,
  },
  {
    name: "frontDiscs",
    label: "Front Pads & Discs",
    type: "radio",
    active: true,
    options: defaultOptionsInspection,
    required: true,
  },
  {
    name: "frontSuspensionInspection",
    label: "Front Suspension inspection (External Mechanic)",
    type: "radio",
    active: true,
    options: defaultOptionsInspection,
    required: true,
  },
  {
    name: "rearTyres",
    label: "Rear Tyres",
    type: "radio",
    active: true,
    options: defaultOptionsInspection,
    required: true,
  },
  {
    name: "rearPads",
    label: "Rear Pads",
    type: "radio",
    active: true,
    options: defaultOptionsInspection,
    required: true,
  },
  {
    name: "rearDiscs",
    label: "Rear Pads & Discs",
    type: "radio",
    active: true,
    options: defaultOptionsInspection,
    required: true,
  },
  {
    name: "rearSuspensionInspection",
    label: "Rear Suspension Inspection (External Mechanic)",
    type: "radio",
    active: true,
    options: defaultOptionsInspection,
    required: true,
  },
  {
    name: "oilLeaks",
    label: "Oil Leaks (External Mechanic)",
    type: "radio",
    active: true,
    options: defaultOptionsInspection,
    required: true,
  },
  // {
  //   name: "serviceHistoryBooksAndKeys",
  //   label: "General – Service History/Books and keys",
  //   type: "radio",
  //   active: true,
  //   options: defaultOptions,
  //   required: true,
  // },
  {
    name: "photos",
    label: "Take photos",
    type: "photos",
    active: true,
  },
];

// Arrival form view component
/* Schema for recon-form-arrival
{
    "model": "718",
    "gpsSerial": null,
    "createdBy": "Stefanie Mona",
    "branchId": 13,
    "createdAt": 1750291742,
    "vin": "WP0ZZZ98ZMS235955",
    "createdById": "qUQYmRAskTNr7xekDbJe9kVLRak1",
    "branchName": "D1 Vic Reco",
    "createdByEmail": "s.mona@duttonone.com.au",
    "hasGPS": "No",
    "make": "PORSCHE",
    "createdByPhone": "+61414563942",
    "yard": "FREIGHTIN",
    "year": 2021,
    "odometer": 41000,
    "stockNumber": 544491
}

*/
const ArrivalFormView = ({ data }) => {
  if (!data) return <p>No Arrival Form Data Found</p>;

  const fields = [
    // { label: "Stock Number", key: "stockNumber" },
    // { label: "VIN", key: "vin" },
    // { label: "Make", key: "make" },
    // { label: "Model", key: "model" },
    // { label: "Year", key: "year" },
    { label: "Odometer", key: "odometer" },
    { label: "Has GPS", key: "hasGPS" },
    { label: "GPS Serial", key: "gpsSerial" },
    { label: "Yard", key: "yard" },
    { label: "Branch Name", key: "branchName" },
    { label: "Created By", key: "createdBy" },
    { label: "Created By Email", key: "createdByEmail" },
    { label: "Created By Phone", key: "createdByPhone" },
    { label: "Created At", key: "createdAt" },
  ];

  return (
    <Table striped bordered hover>
      <thead>
        <tr>
          <th colSpan={2}>Arrival Form</th>
        </tr>
      </thead>
      <tbody>
        {fields.map((field) => (
          <tr key={field.key}>
            <td style={{ textAlign: "left" }}>{field.label}</td>
            <td>
              {field.key === "createdAt"
                ? data.createdAt
                  ? new Date(data.createdAt * 1000).toLocaleString()
                  : "N/A"
                : data[field.key] !== undefined &&
                  data[field.key] !== null &&
                  data[field.key] !== ""
                ? data[field.key]
                : "N/A"}
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

// Inspection form view component
/* Schema for recon-form-inspection
{
    "rearDiscs": 0,
    "oilLeaks": 0,
    "serviceHistory": 2,
    "yard": "VICWaitRep",
    "id": "OPlRSJ5T9CLt5bPjn3eU",
    "year": 2022,
    "createdAt": 1753919689,
    "createdById": "47I5ea1JKIguOnpXXTfCYrskS2m1",
    "stockNumber": 544937,
    "createdByPhone": null,
    "externalScratchesOrDents": 1,
    "rearPads": 0,
    "rearTyres": 1,
    "createdByEmail": "abaranello@duttongroup.com.au",
    "frontPads": 0,
    "rearSuspensionInspection": 0,
    "branchId": 13,
    "frontTyres": 1,
    "make": "RAM",
    "odometer": 18500,
    "frontSuspensionInspection": 0,
    "branchName": "D1 Vic Reco",
    "photos": [],
    "model": "1500",
    "numberOfKeys": 2,
    "frontDiscs": 0,
    "createdBy": "Alec Baranello",
    "vin": "1C6SRFJTXNN422224",
    "accidentSigns": 0
}
*/
const InspectionFormView = ({ data }) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return <p>No Inspection Form Data Found</p>;
  }

  // Define the fields to display
  const fields = [
    // { label: "Stock Number", key: "stockNumber" },
    // { label: "VIN", key: "vin" },
    // { label: "Make", key: "make" },
    // { label: "Model", key: "model" },
    // { label: "Year", key: "year" },
    // { label: "Odometer", key: "odometer" },
    { label: "Yard", key: "yard" },
    { label: "Branch Name", key: "branchName" },
    { label: "Created By", key: "createdBy" },
    // { label: "Created By Email", key: "createdByEmail" },
    { label: "Created By Phone", key: "createdByPhone" },
    { label: "Created At", key: "createdAt" },
    { label: "External Scratches or Dents", key: "externalScratchesOrDents" },
    { label: "Accident Signs", key: "accidentSigns" },
    { label: "Service History", key: "serviceHistory" },
    { label: "Number of Keys", key: "numberOfKeys" },
    { label: "Front Tyres", key: "frontTyres" },
    { label: "Rear Tyres", key: "rearTyres" },
    { label: "Front Pads", key: "frontPads" },
    { label: "Rear Pads", key: "rearPads" },
    { label: "Front Discs", key: "frontDiscs" },
    { label: "Rear Discs", key: "rearDiscs" },
    { label: "Front Suspension Inspection", key: "frontSuspensionInspection" },
    { label: "Rear Suspension Inspection", key: "rearSuspensionInspection" },
  ];

  return (
    <Table striped bordered hover>
      <thead>
        <tr>
          <th style={{ textAlign: "left" }}>Field</th>
          {data.map((form, idx) => (
            <th key={idx}>Form {idx + 1}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {fields.map((field) => (
          <tr key={field.key}>
            <td style={{ textAlign: "left" }}>{field.label}</td>
            {data.map((form, idx) => {
              let value = form[field.key];
              if (field.key === "createdAt") {
                value = value ? new Date(value * 1000).toLocaleString() : "N/A";
              } else if (
                typeof value === "undefined" ||
                value === null ||
                value === ""
              ) {
                value = "N/A";
              }
              // If field is in formFieldsInspection, show label from looked up value
              const fieldDef = formFieldsInspection.find(
                (f) => f.name === field.key
              );
              if (fieldDef && fieldDef.options) {
                const option = fieldDef.options.find(
                  (opt) => opt.value === value
                );
                value = option ? option.label : value;
              }
              return <td key={idx}>{value}</td>;
            })}
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

// QA form view component
/* Schema for recon-form-qa
{
    "checklistOutcome": 0,
    "stockNumber": 543253,
    "windowCleaning": 0,
    "createdByEmail": "msingh@duttongroup.com.au",
    "polishingAndWaxing": 1,
    "minorRepairs": 1,
    "year": 2017,
    "washingAndDrying": 1,
    "dashboardAndConsoleCleaning": 0,
    "wheelAndTyreCleaning": 0,
    "branchId": 13,
    "yard": "MagicWash",
    "leatherConditioning": 0,
    "createdByPhone": null,
    "checklistOutcomeText": "Failed",
    "odometer": 42210,
    "model": "C-CLASS",
    "paintDecontamination": 1,
    "upholsteryCleaning": 1,
    "paintCorrection": 1,
    "createdBy": "Mani Singh",
    "deodorisation": 1,
    "createdById": "OahlykGrfDWdGZZKB2e2SceTfjc2",
    "createdAt": 1760067104,
    "vin": "WDD2050872F601901",
    "vacuumingAndCleaning": 1,
    "branchName": "D1 Vic Reco",
    "make": "MERCEDES-BENZ",
    "compartmentCleaning": 0,
    "interiorGlassPolishing": 0,
    "sanitisation": 1,
    "bugAndTarRemoval": 1
}
*/

const QAFormView = ({ data }) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return <p>No QA Form Data Found</p>;
  }

  const fields = [
    // { label: "Stock Number", key: "stockNumber" },
    // { label: "VIN", key: "vin" },
    // { label: "Make", key: "make" },
    // { label: "Model", key: "model" },
    // { label: "Year", key: "year" },
    // { label: "Odometer", key: "odometer" },
    { label: "Yard", key: "yard" },
    { label: "Branch Name", key: "branchName" },
    { label: "Created By", key: "createdBy" },
    // { label: "Created By Email", key: "createdByEmail" },
    // { label: "Created By Phone", key: "createdByPhone" },
    { label: "Created At", key: "createdAt" },
    { label: "Checklist Outcome", key: "checklistOutcomeText" },
    // Exterior
    ...formFieldsExterior.map((f) => ({
      label: `Exterior - ${f.label}`,
      key: f.name,
      isExterior: true,
    })),
    // Interior
    ...formFieldsInterior.map((f) => ({
      label: `Interior - ${f.label}`,
      key: f.name,
      isInterior: true,
    })),
  ];

  return (
    <Table striped bordered hover>
      <thead>
        <tr>
          <th style={{ textAlign: "left" }}>Field</th>
          {data.map((form, idx) => (
            <th key={idx}>Form {idx + 1}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {fields.map((field) => (
          <tr key={field.key}>
            <td style={{ textAlign: "left" }}>{field.label}</td>
            {data.map((form, idx) => {
              let value = form[field.key];
              if (field.key === "createdAt") {
                value = value ? new Date(value * 1000).toLocaleString() : "N/A";
              } else if (
                typeof value === "undefined" ||
                value === null ||
                value === ""
              ) {
                value = "N/A";
              }
              // If field is in formFieldsExterior or formFieldsInterior, show label from looked up value
              const fieldDef =
                formFieldsExterior.find((f) => f.name === field.key) ||
                formFieldsInterior.find((f) => f.name === field.key);
              if (fieldDef && fieldDef.options) {
                const option = fieldDef.options.find(
                  (opt) => opt.value === value
                );
                value = option ? option.label : value;
              }
              // Set background color for exterior/interior fields
              if (field.isExterior || field.isInterior) {
                value = (
                  <span
                    style={{
                      backgroundColor: field.isExterior
                        ? "lightblue"
                        : "lightgreen",
                      padding: "2px 6px",
                      borderRadius: "4px",
                    }}
                  >
                    {value}
                  </span>
                );
              }
              return <td key={idx}>{value}</td>;
            })}
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

//   RWC form view component
/* Schema for recon-form-rwc
{
    "jobClosedById": "47I5ea1JKIguOnpXXTfCYrskS2m1",
    "jobClosedBy": "Alec Baranello",
    "clockOut": 1758577546,
    "createdById": "47I5ea1JKIguOnpXXTfCYrskS2m1",
    "updatedAt": 1758577546,
    "createdByPhone": null,
    "clockIn": 1758577542,
    "type": "RWC-ClockIn",
    "yard": "VICRWC",
    "createdBy": "Alec Baranello",
    "updatedByEmail": "abaranello@duttongroup.com.au",
    "updatedById": "47I5ea1JKIguOnpXXTfCYrskS2m1",
    "jobClosedByPhone": null,
    "createdByEmail": "abaranello@duttongroup.com.au",
    "jobClosedByEmail": "abaranello@duttongroup.com.au",
    "jobClosed": true,
    "typeId": 1,
    "vin": "WVGZZZC1ZPY086845",
    "createdAt": 1758577542,
    "jobClosedAt": 1758577546,
    "updatedByPhone": null,
    "make": "VOLKSWAGEN",
    "year": 2023,
    "model": "T-CROSS",
    "stockNumber": 531051,
    "updatedBy": "Alec Baranello",
    "branchId": 13,
    "nextYardLocationExpectedReturnDate": 1758722400,
    "odometer": null,
    "nextYardLocation": "WShopOth",
    "branchName": "D1 Vic Reco"
}
    */

const RWCFormView = ({ data }) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return <p>No RWC Form Data Found</p>;
  }

  const fields = [
    // { label: "Stock Number", key: "stockNumber" },
    // { label: "VIN", key: "vin" },
    // { label: "Make", key: "make" },
    // { label: "Model", key: "model" },
    // { label: "Year", key: "year" },
    // { label: "Odometer", key: "odometer" },
    { label: "Yard", key: "yard" },
    { label: "Branch Name", key: "branchName" },
    { label: "Created By", key: "createdBy" },
    // { label: "Created By Email", key: "createdByEmail" },
    // { label: "Created By Phone", key: "createdByPhone" },
    { label: "Created At", key: "createdAt" },
    { label: "Clock In", key: "clockIn" },
    { label: "Clock Out", key: "clockOut" },
    // Total time between clock in and clock out in minutes
    {
      label: "Total Time (minutes)",
      key: "totalTime",
    },
    { label: "Job Closed", key: "jobClosed" },
    { label: "Job Closed By", key: "jobClosedBy" },
    { label: "Job Closed By Email", key: "jobClosedByEmail" },
    { label: "Job Closed By Phone", key: "jobClosedByPhone" },
    { label: "Job Closed At", key: "jobClosedAt" },
    { label: "Next Yard Location", key: "nextYardLocation" },
    {
      label: "Next Yard Location Expected Return Date",
      key: "nextYardLocationExpectedReturnDate",
    },
    { label: "Updated By", key: "updatedBy" },
    // { label: "Updated By Email", key: "updatedByEmail" },
    // { label: "Updated By Phone", key: "updatedByPhone" },
    { label: "Updated At", key: "updatedAt" },
  ];

  return (
    <Table striped bordered hover>
      <thead>
        <tr>
          <th style={{ textAlign: "left" }}>Field</th>
          {data.map((form, idx) => (
            <th key={idx}>Form {idx + 1}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {fields.map((field) => (
          <tr key={field.key}>
            <td style={{ textAlign: "left" }}>{field.label}</td>
            {data.map((form, idx) => {
              let value = form[field.key];
              if (
                field.key === "createdAt" ||
                field.key === "clockIn" ||
                field.key === "clockOut" ||
                field.key === "jobClosedAt" ||
                field.key === "updatedAt" ||
                field.key === "nextYardLocationExpectedReturnDate"
              ) {
                value = value ? new Date(value * 1000).toLocaleString() : "N/A";
              } else if (field.key === "totalTime") {
                if (form.clockIn && form.clockOut) {
                  const diffSeconds = form.clockOut - form.clockIn;
                  value = Math.round(diffSeconds / 60); // in minutes
                } else {
                  value = "N/A";
                }
              } else if (field.key === "jobClosed") {
                value = value ? "Yes" : "No";
              } else if (
                typeof value === "undefined" ||
                value === null ||
                value === ""
              ) {
                value = "N/A";
              }
              return <td key={idx}>{value}</td>;
            })}
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

const ReconReportComponent = ({ vin, stockNumber }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  // Hooks
  const firestore = useFirestore();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch the main reconditioning-process document by VIN or Stock Number
        let reconDocRef;
        let hash = null;
        if (vin && stockNumber) {
          const stockVin = `${stockNumber}${vin}`;
          hash = CryptoJS.MD5(stockVin).toString();
          console.log(stockVin, hash);
        } else {
          setError("Please provide both VIN and Stock Number.");
          setLoading(false);
          return;
        }

        let tmpNotFound = true;

        // Fetch all sub-collections
        const subCollections = [
          "recon-form-arrival",
          "recon-form-inspection",
          "recon-form-rwc",
          "recon-form-qa",
          "recon-form-photo",
        ];
        const subCollectionData = {};
        for (const subCol of subCollections) {
          const subColRef = collection(
            firestore,
            "reconditioning-process",
            hash,
            subCol
          );
          const subColSnapshot = await getDocs(subColRef);
          if (!subColSnapshot.empty) {
            subCollectionData[subCol] = [];
            subColSnapshot.forEach((doc) => {
              subCollectionData[subCol].push(doc.data());
            });
            tmpNotFound = tmpNotFound && false;
          } else {
            subCollectionData[subCol] = null; // No data found
            tmpNotFound = tmpNotFound && true;
            console.log(`No documents found in sub-collection: ${subCol}`);
          }
        }
        console.log("Fetched sub-collection data:", subCollectionData);
        setNotFound(tmpNotFound);

        setReportData({
          subCollectionData,
        });
        setLoading(false);
      } catch (err) {
        console.error("Error fetching reconditioning report data:", err);
        setError("Error fetching reconditioning report data.");
        setLoading(false);
      }
    };

    fetchData();
  }, [vin, stockNumber]);

  const renderForm = (subCol, data) => {
    switch (subCol) {
      case "recon-form-arrival":
        return <ArrivalFormView data={data[0]} />;
        break;

      case "recon-form-inspection":
        return <InspectionFormView data={data} />;
        break;

      case "recon-form-qa":
        return <QAFormView data={data} />;
        break;

      case "recon-form-rwc":
        return <RWCFormView data={data} />;
        break;

      // Add cases for other sub-collections with their respective components
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Container className="mt-4">
        <Row>
          <Col className="text-center">
            <Spinner animation="border" role="status" size="sm" />
          </Col>
        </Row>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-4">
        <Row>
          <Col className="text-center text-danger">{error}</Col>
        </Row>
      </Container>
    );
  }

  if (notFound) {
    return <span>No reconditioning report data available.</span>;
  }

  return (
    <Container className="mt-4">
      <Row>
        <Col>
          <h3>Reconditioning Report</h3>
          {reportData ? (
            <>
              <h5 className="mt-4">Main Reconditioning Data</h5>

              {Object.entries(reportData.subCollectionData).map(
                ([subCol, data]) => (
                  <div key={subCol} className="mt-4">
                    <h5>{subCol.replace(/-/g, " ").toUpperCase()}</h5>
                    {data ? renderForm(subCol, data) : <p>No data found.</p>}
                  </div>
                )
              )}
            </>
          ) : (
            <p>No report data available.</p>
          )}
        </Col>
      </Row>
      {/* <Row className="mt-4">
        <Col className="text-center">
          {reportData ? (
            <>
              <div>
                <Button
                  variant="primary"
                  onClick={() => window.print()}
                  className="me-2"
                >
                  Print Report
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    // Close the report view - implementation depends on how this component is used
                    window.history.back();
                  }}
                >
                  Close
                </Button>
              </div>
            </>
          ) : (
            <p>No report data available.</p>
          )}
        </Col>
      </Row> */}
    </Container>
  );
};

export default ReconReportComponent;
